import { useState, useRef, useCallback, useEffect, useMemo } from "react";

const DEFAULT_PAGE_SIZE = 20;

interface FetchResult<T> {
  rows: T[];
  count: number;
  error: string | null;
}

interface DeepLinkOptions {
  page: number;
  setPage: (page: number) => void;
  searchParams: URLSearchParams;
  setSearchParams: (
    fn: (prev: URLSearchParams) => URLSearchParams,
    opts?: { replace?: boolean },
  ) => void;
  ignoreParams?: string[];
  enabled?: boolean;
}

interface EditDeepLinkOptions<T> {
  paramKey?: string;
  getId: (item: T) => string | number;
}

interface UsePaginationOptions<T> {
  fetchFn: (page: number) => Promise<FetchResult<T>>;
  pageSize?: number;
  deepLink?: DeepLinkOptions;
  onPageAdvance?: (page: number) => void;
  editDeepLink?: EditDeepLinkOptions<T>;
}

const DEFAULT_IGNORE = ["sort", "page"];

export function usePagination<T>({
  fetchFn,
  pageSize = DEFAULT_PAGE_SIZE,
  deepLink,
  onPageAdvance,
  editDeepLink,
}: UsePaginationOptions<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const totalCountRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [canLoadPrevious, setCanLoadPrevious] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const itemsRef = useRef<T[]>([]);
  const lowestPageRef = useRef(1);
  const highestPageRef = useRef(1);
  const loadingRef = useRef(false);

  const updateItems = useCallback((newItems: T[]) => {
    itemsRef.current = newItems;
    setItems(newItems);
  }, []);

  const updateItem = useCallback(
    (id: string | number, updater: (existing: T) => T) => {
      const getId = editDeepLink?.getId ?? ((it: T) => (it as any).id);
      updateItems(
        itemsRef.current.map((it) =>
          String(getId(it)) === String(id) ? updater(it) : it,
        ),
      );
    },
    [updateItems, editDeepLink],
  );

  const handleResult = useCallback(
    (result: FetchResult<T>) => {
      if (result.error) {
        setError(result.error);
        return [];
      }
      setError(null);
      setTotalCount(result.count);
      totalCountRef.current = result.count;
      return result.rows;
    },
    [],
  );

  const initLoad = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      loadingRef.current = true;
      const rows = handleResult(await fetchFn(targetPage));
      lowestPageRef.current = targetPage;
      highestPageRef.current = targetPage;
      updateItems(rows);
      setCanLoadPrevious(false);
      setHasMore(rows.length >= pageSize && rows.length < totalCountRef.current);
      setLoading(false);
      loadingRef.current = false;

      if (targetPage > 1) {
        for (let p = targetPage - 1; p >= 1; p--) {
          await new Promise((r) => setTimeout(r, 300));
          const prevRows = handleResult(await fetchFn(p));
          if (prevRows.length > 0) {
            const scrollBefore = document.documentElement.scrollHeight;
            lowestPageRef.current = p;
            updateItems([...prevRows, ...itemsRef.current]);
            await new Promise((r) => requestAnimationFrame(r));
            const scrollAfter = document.documentElement.scrollHeight;
            window.scrollBy(0, scrollAfter - scrollBefore);
          }
        }
      }
    },
    [fetchFn, pageSize, updateItems, handleResult],
  );

  const pageAdvance = useCallback(
    (nextPage: number) => {
      if (deepLink) {
        deepLink.setSearchParams((p) => {
          if (nextPage === 1) p.delete("page");
          else p.set("page", nextPage.toString());
          return p;
        }, { replace: true });
      }
      onPageAdvance?.(nextPage);
    },
    [deepLink, onPageAdvance],
  );

  const loadMore = useCallback(async () => {
    if (loadingRef.current) return;
    const nextPage = highestPageRef.current + 1;
    setLoading(true);
    loadingRef.current = true;
    const rows = handleResult(await fetchFn(nextPage));
    if (rows.length > 0) {
      highestPageRef.current = nextPage;
      const newItems = [...itemsRef.current, ...rows];
      updateItems(newItems);
      setHasMore(rows.length >= pageSize && newItems.length < totalCountRef.current);
      pageAdvance(nextPage);
    } else {
      setHasMore(false);
    }
    setLoading(false);
    loadingRef.current = false;
  }, [fetchFn, pageSize, updateItems, handleResult, pageAdvance]);

  const loadPrevious = useCallback(async () => {
    if (loadingRef.current) return;
    const prevPage = lowestPageRef.current - 1;
    if (prevPage < 1) return;
    setLoadingPrevious(true);
    loadingRef.current = true;
    const rows = handleResult(await fetchFn(prevPage));
    if (rows.length > 0) {
      lowestPageRef.current = prevPage;
      updateItems([...rows, ...itemsRef.current]);
      setCanLoadPrevious(prevPage > 1);
    }
    setLoadingPrevious(false);
    loadingRef.current = false;
  }, [fetchFn, updateItems, handleResult]);

  // Refetch every loaded page without touching `loading` — for background
  // polling (e.g. rows stuck in a 'generating' status) where a spinner
  // flicker on each tick would be noise.
  const silentRefresh = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    const all: T[] = [];
    let failed = false;
    for (let p = lowestPageRef.current; p <= highestPageRef.current; p++) {
      const result = await fetchFn(p);
      if (result.error) {
        failed = true;
        break;
      }
      setTotalCount(result.count);
      totalCountRef.current = result.count;
      all.push(...result.rows);
    }
    if (!failed) {
      updateItems(all);
      setHasMore(all.length < totalCountRef.current);
    }
    loadingRef.current = false;
  }, [fetchFn, updateItems]);

  const isPageLoaded = useCallback(
    (p: number) => p >= lowestPageRef.current && p <= highestPageRef.current,
    [],
  );

  const topSentinelRef = useRef<HTMLDivElement>(null);

  const ignoreParams = deepLink?.ignoreParams ?? DEFAULT_IGNORE;
  const filterParamsKey = useMemo(() => {
    if (!deepLink) return "";
    const p = new URLSearchParams(deepLink.searchParams);
    for (const key of ignoreParams) p.delete(key);
    return p.toString();
  }, [deepLink?.searchParams, ignoreParams]);

  const prevFilterKeyRef = useRef(filterParamsKey);
  useEffect(() => {
    if (!deepLink || deepLink.enabled === false) return;
    if (prevFilterKeyRef.current !== filterParamsKey) {
      prevFilterKeyRef.current = filterParamsKey;
      deepLink.setPage(1);
      initLoad(1);
      return;
    }
    prevFilterKeyRef.current = filterParamsKey;
    // eslint-disable-next-line
  }, [filterParamsKey]);

  const prevPageRef = useRef(deepLink?.page);
  useEffect(() => {
    if (!deepLink || deepLink.enabled === false) return;
    if (prevPageRef.current === deepLink.page) {
      if (itemsRef.current.length === 0) initLoad(deepLink.page);
      return;
    }
    prevPageRef.current = deepLink.page;
    if (!isPageLoaded(deepLink.page)) {
      initLoad(deepLink.page);
    }
    // eslint-disable-next-line
  }, [deepLink?.page, deepLink?.enabled]);

  // Edit deep-link
  const editParamKey = editDeepLink?.paramKey || "edit";
  const [editingItem, setEditingItem] = useState<T | null>(null);

  useEffect(() => {
    if (!deepLink || !editDeepLink) return;
    const editId = deepLink.searchParams.get(editParamKey);
    if (editId && items.length > 0) {
      const item = items.find((i) => String(editDeepLink.getId(i)) === editId);
      if (item && !editingItem) {
        setEditingItem(item);
      }
    } else if (!editId && editingItem) {
      setEditingItem(null);
    }
  }, [items, deepLink?.searchParams.get(editParamKey)]);

  const openEdit = useCallback((item: T) => {
    if (!deepLink || !editDeepLink) {
      setEditingItem(item);
      return;
    }
    setEditingItem(item);
    deepLink.setSearchParams(
      (p) => { p.set(editParamKey, String(editDeepLink.getId(item))); return p; },
      { replace: true },
    );
  }, [deepLink, editDeepLink, editParamKey]);

  const closeEdit = useCallback(() => {
    setEditingItem(null);
    if (deepLink) {
      deepLink.setSearchParams(
        (p) => { p.delete(editParamKey); return p; },
        { replace: true },
      );
    }
  }, [deepLink, editParamKey]);

  return {
    items,
    totalCount,
    loading,
    loadingPrevious,
    hasMore,
    canLoadPrevious,
    error,
    initLoad,
    loadMore,
    loadPrevious,
    silentRefresh,
    updateItem,
    isPageLoaded,
    topSentinelRef,
    editingItem,
    openEdit,
    closeEdit,
  };
}
